import { endWelcome, welcomePlayId } from '@/stores/welcome.store';
import { For, Show, createEffect, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';

// UCH-427: fireworks + confetti overlay for the first-login Welcome.
// Pure CSS keyframes (index.css welcome-*) + randomized inline styles.

const DURATION_MS = 4000;
const BURST_GAP_MS = 500;
const COLORS = ['#2563eb', '#60a5fa', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6'] as const;

const pickColor = () => COLORS[Math.floor(Math.random() * COLORS.length)] ?? COLORS[0];
const between = (min: number, max: number) => min + Math.random() * (max - min);

const makeBursts = () =>
  Array.from({ length: 3 }, () => {
    // two colours per burst, alternating — reads as a real firework
    const colors = [pickColor(), pickColor()];
    return {
      x: between(15, 85),
      y: between(15, 45),
      sparks: Array.from({ length: 24 }, (_, k) => {
        const angle = (2 * Math.PI * k) / 24;
        const radius = between(110, 210);
        return {
          dx: Math.cos(angle) * radius,
          dy: Math.sin(angle) * radius,
          color: colors[k % 2],
        };
      }),
    };
  });

const makeConfetti = () =>
  Array.from({ length: 80 }, () => ({
    left: between(0, 100),
    color: pickColor(),
    delay: between(0, 1200),
    duration: between(2200, 3200),
    drift: between(-80, 80),
    width: between(6, 10),
    height: between(8, 14),
  }));

// Read at play time, not once at mount: always current, no listener needed.
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export function WelcomeCelebration() {
  // End the celebration after its run even when reduced motion skips the
  // visuals, so a debug-forced banner doesn't stick around.
  createEffect(() => {
    if (welcomePlayId() === 0) return;
    const timer = window.setTimeout(endWelcome, DURATION_MS);
    onCleanup(() => window.clearTimeout(timer));
  });

  // keyed on playId: a replay re-mounts the overlay with fresh randomness.
  return (
    <Show when={prefersReducedMotion() ? 0 : welcomePlayId()} keyed>
      <Portal>
        <div
          class="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
          aria-hidden="true"
          data-testid="welcome-celebration"
        >
          <For each={makeConfetti()}>
            {(c) => (
              <span
                class="welcome-confetto"
                style={`left:${c.left}%;width:${c.width}px;height:${c.height}px;background-color:${c.color};animation-delay:${c.delay}ms;animation-duration:${c.duration}ms;--drift:${c.drift}px`}
              />
            )}
          </For>
          <For each={makeBursts()}>
            {(b, i) => (
              <div class="absolute" style={`left:${b.x}%;top:${b.y}%`}>
                <For each={b.sparks}>
                  {(s) => (
                    <span
                      class="welcome-spark"
                      style={`color:${s.color};animation-delay:${i() * BURST_GAP_MS}ms;--dx:${s.dx}px;--dy:${s.dy}px`}
                    />
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      </Portal>
    </Show>
  );
}
